import { useState, useEffect, useRef, useCallback } from 'react';
import { mediaAPI, IMAGE_BASE } from '../../services/api';
import { Upload, Trash2, Search, CheckSquare, Square, Info, X } from 'lucide-react';

export default function MediaLibraryAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [usageModal, setUsageModal] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const fileInputRef = useRef(null);
    const searchTimeout = useRef(null);

    const fetchMedia = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mediaAPI.getAll({ page, limit: 50, search: search || undefined });
            setItems(res.data.items || []);
            setTotal(res.data.total || 0);
            setTotalPages(res.data.totalPages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [page, search]);

    useEffect(() => { fetchMedia(); }, [fetchMedia]);

    const handleSearchChange = (val) => {
        setSearchInput(val);
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setSearch(val);
            setPage(1);
        }, 400);
    };

    const handleUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        setUploadProgress(0);
        try {
            const fd = new FormData();
            for (const file of files) {
                fd.append('images', file);
            }
            await mediaAPI.upload(fd, (e) => {
                if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
            });
            setPage(1);
            fetchMedia();
        } catch (err) {
            alert(err.response?.data?.error || 'Yükleme hatası');
        }
        setUploading(false);
        setUploadProgress(0);
    };

    const handleFileSelect = (e) => {
        handleUpload(Array.from(e.target.files));
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        if (files.length > 0) handleUpload(files);
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        if (selected.size === items.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(items.map(i => i.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        if (!confirm(`${selected.size} görseli silmek istediğinize emin misiniz?`)) return;
        setDeleting(true);
        try {
            const res = await mediaAPI.bulkDelete(Array.from(selected));
            alert(res.data.message);
            setSelected(new Set());
            fetchMedia();
        } catch (err) {
            alert(err.response?.data?.error || 'Silme hatası');
        }
        setDeleting(false);
    };

    const handleSingleDelete = async (item) => {
        if (!confirm(`"${item.filename}" görselini silmek istediğinize emin misiniz?`)) return;
        try {
            await mediaAPI.delete(item.id);
            setSelected(prev => { const n = new Set(prev); n.delete(item.id); return n; });
            fetchMedia();
        } catch (err) {
            alert(err.response?.data?.error || 'Silinemedi');
        }
    };

    const showUsage = async (item) => {
        setUsageModal({ ...item, usage: null });
        setUsageLoading(true);
        try {
            const res = await mediaAPI.getUsage(item.id);
            setUsageModal({ ...item, usage: res.data.usage });
        } catch (e) {
            setUsageModal({ ...item, usage: { total: 0, productImages: 0, variantImages: 0 } });
        }
        setUsageLoading(false);
    };

    const formatSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div>
            <div className="admin-header">
                <h1>Görsel Havuzu</h1>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{total} görsel</span>
                    <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                        <Upload size={16} style={{ marginRight: 4 }} />
                        {uploading ? `Yükleniyor %${uploadProgress}` : 'Görsel Yükle'}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                </div>
            </div>

            {/* Search + Actions bar */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: '1 1 250px', maxWidth: 350 }}>
                    <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        className="form-control"
                        placeholder="Dosya adı ile ara..."
                        value={searchInput}
                        onChange={e => handleSearchChange(e.target.value)}
                        style={{ paddingLeft: 32 }}
                    />
                </div>
                {items.length > 0 && (
                    <button className="btn btn-secondary" onClick={selectAll} style={{ fontSize: '0.85rem' }}>
                        {selected.size === items.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </button>
                )}
                {selected.size > 0 && (
                    <button className="btn btn-danger" onClick={handleBulkDelete} disabled={deleting} style={{ fontSize: '0.85rem' }}>
                        <Trash2 size={14} style={{ marginRight: 4 }} />
                        {deleting ? 'Siliniyor...' : `${selected.size} Görseli Sil`}
                    </button>
                )}
            </div>

            {/* Drag-drop zone + Grid */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                style={{
                    border: dragOver ? '2px dashed var(--primary)' : '2px dashed transparent',
                    borderRadius: 12,
                    padding: dragOver ? '1rem' : 0,
                    background: dragOver ? 'var(--primary-light, rgba(99,102,241,0.05))' : 'transparent',
                    transition: 'all 0.2s',
                    minHeight: 200
                }}
            >
                {dragOver && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--primary)', fontWeight: 600, fontSize: '1.1rem' }}>
                        Görselleri buraya bırakın...
                    </div>
                )}

                {loading ? (
                    <div className="loading"><div className="spinner"></div></div>
                ) : items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                        <Upload size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                        <p>Henüz görsel eklenmemiş</p>
                        <p style={{ fontSize: '0.85rem' }}>Görselleri sürükleyip bırakın veya yükle butonunu kullanın</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '0.75rem'
                    }}>
                        {items.map(item => (
                            <div
                                key={item.id}
                                style={{
                                    position: 'relative',
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    border: selected.has(item.id) ? '2px solid var(--primary)' : '2px solid var(--border)',
                                    background: 'var(--card-bg, #fff)',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.15s'
                                }}
                                onClick={() => toggleSelect(item.id)}
                            >
                                <div style={{ position: 'relative', paddingTop: '100%' }}>
                                    <img
                                        src={IMAGE_BASE + item.url}
                                        alt={item.filename}
                                        style={{
                                            position: 'absolute',
                                            top: 0, left: 0, width: '100%', height: '100%',
                                            objectFit: 'cover'
                                        }}
                                        loading="lazy"
                                    />
                                    {/* Checkbox overlay */}
                                    <div style={{
                                        position: 'absolute', top: 6, left: 6,
                                        background: selected.has(item.id) ? 'var(--primary)' : 'rgba(255,255,255,0.85)',
                                        borderRadius: 4, padding: 2,
                                        color: selected.has(item.id) ? '#fff' : 'var(--text-muted)'
                                    }}>
                                        {selected.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </div>
                                    {/* Action buttons */}
                                    <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); showUsage(item); }}
                                            style={{
                                                background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 4,
                                                padding: '3px 5px', cursor: 'pointer', color: 'var(--text-muted)'
                                            }}
                                            title="Kullanım bilgisi"
                                        >
                                            <Info size={14} />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleSingleDelete(item); }}
                                            style={{
                                                background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 4,
                                                padding: '3px 5px', cursor: 'pointer', color: 'var(--danger)'
                                            }}
                                            title="Sil"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div style={{ padding: '6px 8px' }}>
                                    <div style={{
                                        fontSize: '0.75rem', fontWeight: 500,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                    }}>
                                        {item.filename}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                        {formatSize(item.fileSize)}
                                        {item.width && item.height ? ` | ${item.width}x${item.height}` : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button
                        className="btn btn-secondary"
                        disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}
                        style={{ fontSize: '0.85rem' }}
                    >
                        Önceki
                    </button>
                    <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {page} / {totalPages}
                    </span>
                    <button
                        className="btn btn-secondary"
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                        style={{ fontSize: '0.85rem' }}
                    >
                        Sonraki
                    </button>
                </div>
            )}

            {/* Usage Modal */}
            {usageModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setUsageModal(null)}>
                    <div className="modal" style={{ maxWidth: 450 }}>
                        <div className="modal-header">
                            <h2>Görsel Bilgisi</h2>
                            <button className="modal-close" onClick={() => setUsageModal(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                                <img
                                    src={IMAGE_BASE + usageModal.url}
                                    alt={usageModal.filename}
                                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }}
                                />
                            </div>
                            <table style={{ width: '100%', fontSize: '0.85rem' }}>
                                <tbody>
                                    <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Dosya Adı</td><td style={{ padding: '4px 8px' }}>{usageModal.filename}</td></tr>
                                    <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Boyut</td><td style={{ padding: '4px 8px' }}>{formatSize(usageModal.fileSize)}</td></tr>
                                    {usageModal.width && <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>Boyutlar</td><td style={{ padding: '4px 8px' }}>{usageModal.width}x{usageModal.height}px</td></tr>}
                                    <tr><td style={{ fontWeight: 600, padding: '4px 8px' }}>URL</td><td style={{ padding: '4px 8px', wordBreak: 'break-all', fontSize: '0.75rem' }}>{usageModal.url}</td></tr>
                                </tbody>
                            </table>
                            {usageLoading ? (
                                <div style={{ textAlign: 'center', padding: '1rem' }}><div className="spinner"></div></div>
                            ) : usageModal.usage && (
                                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 8 }}>
                                    <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Kullanım Durumu</div>
                                    {usageModal.usage.total === 0 ? (
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Kullanılmıyor</span>
                                    ) : (
                                        <div style={{ fontSize: '0.85rem' }}>
                                            <div>Ürün görseli: {usageModal.usage.productImages} yerde</div>
                                            <div>Varyant görseli: {usageModal.usage.variantImages} yerde</div>
                                            <div style={{ fontWeight: 600, marginTop: 4 }}>Toplam: {usageModal.usage.total} yerde kullanılıyor</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setUsageModal(null)}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload progress overlay */}
            {uploading && (
                <div style={{
                    position: 'fixed', bottom: 24, right: 24,
                    background: 'var(--card-bg, #fff)', border: '1px solid var(--border)',
                    borderRadius: 12, padding: '1rem 1.5rem',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    zIndex: 1000, minWidth: 250
                }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Yükleniyor... %{uploadProgress}</div>
                    <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div style={{
                            width: `${uploadProgress}%`, height: '100%',
                            background: 'var(--primary)', transition: 'width 0.3s',
                            borderRadius: 4
                        }} />
                    </div>
                </div>
            )}
        </div>
    );
}
